import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, CheckCheck, Eye, ShieldAlert, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmReasonDialog } from "@/components/common/ConfirmReasonDialog";
import { EmptyState, ErrorState, LoadingState, Notice, PageHeader, StatusBadge } from "@/components/common/States";
import { api, errorMessage, fetcher } from "@/lib/api";
import { REPORT_STATE, timeAgo } from "@/lib/labels";

export default function PlatformModeration() {
  const [status, setStatus] = useState("open");
  const [tindak, setTindak] = useState(null);
  const [suspendSchool, setSuspendSchool] = useState(null);
  const q = useQuery({ queryKey: ["platform-reports", status], queryFn: () => fetcher(`/platform/feed/reports?status=${status}`) });
  const ringkas = useQuery({ queryKey: ["platform-moderation"], queryFn: () => fetcher("/platform/feed/moderation") });

  const segarkan = () => {
    q.refetch();
    ringkas.refetch();
  };

  const putus = useMutation({
    mutationFn: async ({ id, action, hide, note }) => (await api.post(`/platform/feed/reports/${id}/resolve`,
      { action, hide_target: !!hide, note: note || null })).data,
    onSuccess: (d) => {
      toast.success(d.status === "resolved"
        ? (d.target ? "Laporan ditindaklanjuti dan konten disembunyikan." : "Laporan ditandai selesai.")
        : "Laporan ditolak.");
      segarkan();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const suspend = useMutation({
    mutationFn: async ({ schoolId, reason }) => (await api.post(`/platform/feed/schools/${schoolId}/suspend-cross-school`,
      { reason })).data,
    onSuccess: () => {
      toast.success("Sekolah ditangguhkan dari fitur lintas sekolah.");
      setSuspendSchool(null);
      segarkan();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const restore = useMutation({
    mutationFn: async (schoolId) => (await api.post(`/platform/feed/schools/${schoolId}/restore-cross-school`)).data,
    onSuccess: () => {
      toast.success("Sekolah dipulihkan ke fitur lintas sekolah.");
      segarkan();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const m = ringkas.data;
  return (
    <div className="space-y-6" data-testid="platform-moderation">
      <PageHeader eyebrow="Moderasi Platform" title="Tinjauan Konten Lintas Sekolah"
        description="Kelola laporan dan penangguhan sekolah untuk fitur lintas sekolah." />

      {m && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="platform-moderation-counts">
          {[
            ["Laporan menunggu", m.open_reports],
            ["Postingan ditarik", m.hidden_posts],
            ["Komentar disembunyikan", m.hidden_comments],
            ["Sekolah ditangguhkan", m.suspended_schools]
          ].map(([label, n]) => (
            <div key={label} className="rounded-xl border bg-card p-3">
              <p className="font-brand text-2xl font-extrabold tabular-nums">{n}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>
      )}

      <Notice tone="muted" testId="platform-moderation-hint">
        Moderasi platform hanya berlaku untuk konten lintas sekolah. Konten internal sekolah dikelola oleh moderator sekolah masing-masing.
      </Notice>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Saring laporan">
        {[["open", "Menunggu"], ["resolved", "Ditindaklanjuti"], ["dismissed", "Ditolak"], ["all", "Semua"]].map(([v, label]) => (
          <Button key={v} size="sm" variant={status === v ? "default" : "outline"} onClick={() => setStatus(v)} data-testid={`report-filter-${v}`}>
            {label}
          </Button>
        ))}
      </div>

      {q.isLoading ? <LoadingState rows={3} /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} />
        : !q.data.reports.length ? (
          <EmptyState title={status === "open" ? "Tidak ada laporan yang menunggu" : "Belum ada laporan pada saringan ini"}
            description="Laporan konten lintas sekolah akan muncul di sini." />
        ) : (
          <div className="space-y-3" data-testid="platform-reports-list">
            {q.data.reports.map((r) => (
              <div key={r.id} className={`rounded-xl border bg-card p-4 ${r.status === "resolved" ? "border-green-200 dark:border-green-900" : ""} ${r.status === "dismissed" ? "border-gray-200 dark:border-gray-800" : ""}`} data-testid={`platform-report-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {r.ticket_no && (
                        <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-mono font-semibold text-[#0B3D91] dark:bg-blue-950 dark:text-blue-200" data-testid="report-ticket">
                          {r.ticket_no}
                        </span>
                      )}
                      {r.severity && (
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                          r.severity === "kritis" ? "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200" :
                          r.severity === "tinggi" ? "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200" :
                          r.severity === "sedang" ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200" :
                          "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200"
                        }`} data-testid="report-severity">
                          {r.severity.toUpperCase()}
                        </span>
                      )}
                      <StatusBadge map={REPORT_STATE} value={r.status} testId="report-status" />
                    </div>
                    <p className="text-sm font-medium mb-1" data-testid="report-category">Kategori: {r.category}</p>
                    {r.note && <p className="text-sm text-muted-foreground mb-2" data-testid="report-note">{r.note}</p>}
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>Dilaporkan oleh: <span className="font-medium">{r.reporter_name}</span> dari <span className="font-medium">{r.school_name}</span></p>
                      <p>Konten dari: <span className="font-medium">{r.target_school_name}</span></p>
                      <p>Waktu laporan: {timeAgo(r.created_at)}</p>
                      {r.due_at && <p className="text-[#E11D2E] font-medium">Tenggat: {new Date(r.due_at).toLocaleDateString("id-ID", {day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"})}</p>}
                    </div>
                    {r.resolved_at && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        <p>Diselesaikan: {timeAgo(r.resolved_at)} oleh {r.resolved_by_name}</p>
                        {r.resolution_note && <p>Catatan: {r.resolution_note}</p>}
                      </div>
                    )}
                  </div>
                  {r.status === "open" && (
                    <div className="flex flex-col gap-2">
                      <Button size="sm" variant="outline" disabled={putus.isPending} onClick={() => setTindak({ id: r.id, action: "resolve", hide: false })} data-testid="report-dismiss-button">
                        <X className="mr-1.5 h-4 w-4" /> Tolak
                      </Button>
                      <Button size="sm" variant="outline" disabled={putus.isPending} onClick={() => setTindak({ id: r.id, action: "resolve", hide: true })} data-testid="report-hide-button">
                        <Eye className="mr-1.5 h-4 w-4" /> Sembunyikan
                      </Button>
                      <Button size="sm" disabled={putus.isPending} onClick={() => setTindak({ id: r.id, action: "resolve", hide: false })} data-testid="report-resolve-button">
                        <CheckCheck className="mr-1.5 h-4 w-4" /> Selesai
                      </Button>
                      <Button size="sm" variant="destructive" disabled={suspend.isPending} onClick={() => setSuspendSchool({ id: r.target_school_id, name: r.target_school_name })} data-testid="report-suspend-button">
                        <AlertTriangle className="mr-1.5 h-4 w-4" /> Tangguhkan Sekolah
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      {q.data?.suspended_schools?.length > 0 && (
        <section className="rounded-xl border bg-card p-4" data-testid="suspended-schools">
          <h3 className="text-base font-bold mb-3">Sekolah yang Ditangguhkan</h3>
          <div className="space-y-2">
            {q.data.suspended_schools.map((s) => (
              <div key={s.school_id} className="flex items-center justify-between gap-3 rounded-lg border p-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{s.school_name}</p>
                  <p className="text-xs text-muted-foreground">Ditangguhkan: {new Date(s.suspended_at).toLocaleDateString("id-ID")}</p>
                  {s.suspension_reason && <p className="text-xs text-muted-foreground mt-1">Alasan: {s.suspension_reason}</p>}
                </div>
                <Button size="sm" variant="outline" disabled={restore.isPending} onClick={() => restore.mutate(s.school_id)} data-testid={`restore-school-${s.school_id}`}>
                  <ShieldAlert className="mr-1.5 h-4 w-4" /> Pulihkan
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      {tindak && (
        <ConfirmReasonDialog open={!!tindak} onOpenChange={(v) => !v && setTindak(null)} testId="report-resolve-dialog"
          title="Tindak laporan" confirmLabel="Kirim keputusan"
          description="Keputusan ini akan dicatat dan notifikasi dikirim ke pelapor."
          onConfirm={async (note) => {
            await putus.mutateAsync({ id: tindak.id, action: tindak.action, hide: tindak.hide, note });
            setTindak(null);
          }} />
      )}

      {suspendSchool && (
        <ConfirmReasonDialog open={!!suspendSchool} onOpenChange={(v) => !v && setSuspendSchool(null)} danger testId="suspend-school-dialog"
          title="Tangguhkan sekolah dari lintas sekolah" confirmLabel="Tangguhkan"
          description={`Sekolah ${suspendSchool.name} tidak lagi dapat mengirim atau menerima postingan lintas sekolah. Sekolah tetap dapat menggunakan fitur internal.`}
          onConfirm={async (reason) => {
            await suspend.mutateAsync({ schoolId: suspendSchool.id, reason });
          }} />
      )}
    </div>
  );
}
